import Flex from '@Components/Bases/Flex';
import {
  CATEGORY_TITLES,
  INFO_CATEGORY_RISK_RELATED,
  riskChanceOptions,
  riskOrder,
} from '@Domains/RiskRelated';
import { useDisclosure } from '@hooks/useDisclosure';
import {
  Box,
  Button,
  CircularProgress,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';

import FormSelect from '@Components/Form/FormSelect';
import { disabledFormInputStyles } from '@Components/Form/styles';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import { useMemo } from 'react';
import { useGoalAndOperationalPlansForm } from '../../GoalAndOperationalPlansFormProvider';
import { IRelateMasterDataFormValues } from '../../GoalAndOperationalPlansFormProvider/types';
import ModalAddRiskRelated from './ModalAddRiskRelated';
import { OTHER_RISKS_ID } from './ModalAddRiskRelated/ModalRiskRelatedCheckboxList';
import { mapRiskByGroupsToUIDisplay } from './ModalAddRiskRelated/utils';

type ChanceOfRiskProps = {
  planIndex: number;
  riskIndex: number;
  isCompletedTh?: boolean;
  isCompletedEn?: boolean;
};

export type RelateRisksWithCategory = IRelateMasterDataFormValues & {
  infoCategory: string;
  sequence: number;
};

const ChanceOfRisk = ({
  planIndex,
  riskIndex,
  isCompletedTh,
  isCompletedEn,
}: ChanceOfRiskProps) => {
  const [opened, { open, close }] = useDisclosure();
  const {
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useGoalAndOperationalPlansForm();

  const relateRisks = watch(
    `companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.risks.${riskIndex}.relateRisks`
  );
  const risksError =
    errors.companyValueIncreasePlans?.valueUpPlans?.[planIndex]?.riskManagement
      ?.risks?.[riskIndex]?.relateRisks;

  const [{ data, loading }] = useGetInfoCategoryMasterData([
    INFO_CATEGORY_RISK_RELATED.STRATEGIC_RISK,
    INFO_CATEGORY_RISK_RELATED.OPERATIONAL_RISK,
    INFO_CATEGORY_RISK_RELATED.COMPLIANCE_RISK,
    INFO_CATEGORY_RISK_RELATED.FINANCIAL_RISK,
  ]);

  const mapRelateRisks = useMemo<RelateRisksWithCategory[]>(() => {
    return (
      relateRisks?.map((risk) => {
        const riskId = risk.infoCategoryMasterDataId;
        const masterData = data?.find(
          (master) => master.id === risk.infoCategoryMasterDataId
        );

        const isOther = Object.values(OTHER_RISKS_ID).includes(riskId);

        return {
          sequence: masterData?.sequence,
          infoCategory: masterData?.infoCategory,
          infoCategoryMasterDataId: masterData?.id,
          nameTh: isOther ? risk.nameTh : masterData?.nameTh,
          nameEn: isOther ? risk.nameEn : masterData?.nameEn,
        } as RelateRisksWithCategory;
      }) ?? []
    );
  }, [data, relateRisks]);

  const riskWithGroups = mapRiskByGroupsToUIDisplay(mapRelateRisks);

  const sortedRiskWithGroups = riskWithGroups.sort((a, b) => {
    return (
      riskOrder.indexOf(a.infoCategory as INFO_CATEGORY_RISK_RELATED) -
      riskOrder.indexOf(b.infoCategory as INFO_CATEGORY_RISK_RELATED)
    );
  });

  const handleUpdateRisks = (selectedRisks: IRelateMasterDataFormValues[]) => {
    setValue(
      `companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.risks.${riskIndex}.relateRisks`,
      selectedRisks
    );
    trigger(
      `companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.risks.${riskIndex}.relateRisks`
    );
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box mt={4}>
      <Flex gap={2}>
        <InputLabel
          sx={{ width: 1, maxWidth: 350, textAlign: 'right' }}
          required
        >
          หัวข้อความเสี่ยงที่เกี่ยวข้อง
        </InputLabel>
        <Box>
          {sortedRiskWithGroups.map((group, index) => (
            <Flex key={index} width={1} gap={3} mt={index === 0 ? 0 : 2}>
              <Box flex={1}>
                <Typography sx={{ textDecoration: 'underline' }}>
                  {CATEGORY_TITLES[group.infoCategory]?.th}
                </Typography>
                {group.data.map((risk) => {
                  const isOther = Object.values(OTHER_RISKS_ID).includes(
                    risk.infoCategoryMasterDataId ?? 0
                  );
                  return (
                    <Typography key={risk.id} component={'li'}>
                      {`${isOther ? 'อื่น ๆ : ' : ''}${risk.nameTh}`}
                    </Typography>
                  );
                })}
              </Box>
              <Box flex={1}>
                <Typography sx={{ textDecoration: 'underline' }}>
                  {CATEGORY_TITLES[group.infoCategory]?.en}
                </Typography>
                {group.data.map((risk) => {
                  const isOther = Object.values(OTHER_RISKS_ID).includes(
                    risk.infoCategoryMasterDataId ?? 0
                  );
                  return (
                    <Typography key={risk.id} component={'li'}>
                      {`${isOther ? 'Other : ' : ''}${risk.nameEn}`}
                    </Typography>
                  );
                })}
              </Box>
            </Flex>
          ))}
          <Flex flexDirection={'column'} alignItems={'start'}>
            <Button
              color="inherit"
              onClick={open}
              sx={{ mt: riskWithGroups.length > 0 ? 2 : 0 }}
              disabled={isCompletedTh || isCompletedEn}
            >
              กรุณาเลือกหัวข้อความเสี่ยงที่เกี่ยวข้อง
            </Button>
            {risksError && (
              <Typography mt={0.5} variant={'caption'} color={'error.main'}>
                {risksError.message}
              </Typography>
            )}
          </Flex>
        </Box>
        {opened && (
          <ModalAddRiskRelated
            open={opened}
            close={close}
            onUpdateRisks={handleUpdateRisks}
            riskSelected={relateRisks ?? []}
          />
        )}
      </Flex>
      <Flex mt={2} gap={2} alignItems={'center'}>
        <InputLabel
          sx={{ width: 1, maxWidth: 350, textAlign: 'right' }}
          required
        >
          โอกาสเกิดความเสี่ยง
        </InputLabel>
        <FormSelect
          name={`companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.risks.${riskIndex}.chance`}
          placeholder="กรุณาเลือกโอกาสเกิดความเสี่ยง"
          required
          sx={
            isCompletedTh || isCompletedEn
              ? { ...disabledFormInputStyles, width: 220 }
              : { width: 220 }
          }
        >
          {riskChanceOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </FormSelect>
      </Flex>
    </Box>
  );
};

export default ChanceOfRisk;
